import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Check, Crown, Infinity as InfinityIcon, FileText, Globe, Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { isAndroidApp } from "@/lib/platform";
import GooglePlayPlan from "@/components/GooglePlayPlan";

const FEATURES = [
  { icon: InfinityIcon, text: "Unlimited active debts" },
  { icon: Bell, text: "Advanced reminders" },
  { icon: FileText, text: "PDF statements" },
  { icon: Globe, text: "Additional currencies" },
  { icon: Crown, text: "Enhanced relationship history" },
];

export default function Upgrade() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const { data: sub } = useQuery({
    queryKey: ["subscription"],
    queryFn: async () => (await api.get("/subscription")).data,
  });

  const isPlus = user?.subscription_status === "plus";
  const androidApp = isAndroidApp();

  // If the server reconciled a paid-but-unactivated subscription, sync the session.
  React.useEffect(() => {
    if (sub?.plan === "plus" && user && user.subscription_status !== "plus") {
      refreshUser();
    }
  }, [sub, user, refreshUser]);

  const upgrade = async () => {
    setLoading(true);
    try {
      // Callback is decided server-side (always the production Debii app).
      const { data } = await api.post("/subscription/initialize", {});
      window.location.href = data.authorization_url;
    } catch (e) {
      if (apiError(e) === "payment_not_configured") {
        toast.error("Payments require production Paystack credentials to be configured. See setup notes.", { duration: 6000 });
      } else {
        toast.error(apiError(e));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="debii-shell min-h-screen px-5 py-4 pb-10">
      <button data-testid="upgrade-back" onClick={() => navigate(-1)} className="h-10 w-10 rounded-full bg-secondary flex items-center justify-center">
        <ArrowLeft className="h-5 w-5 text-primary" />
      </button>

      <div className="mt-6 text-center">
        <div className="mx-auto h-16 w-16 rounded-2xl bg-primary flex items-center justify-center animate-pop">
          <Crown className="h-8 w-8 text-amber-300" />
        </div>
        <h1 className="mt-5 font-display text-3xl font-extrabold text-foreground">Debii Plus</h1>
        <p className="mt-2 text-sm text-muted-foreground">Everything you need to never lose track.</p>
        <div className="mt-5 inline-flex items-baseline gap-1">
          <span className="font-display text-4xl font-extrabold text-foreground">₦1,000</span>
          <span className="text-muted-foreground">/year</span>
        </div>
        <p className="text-xs text-muted-foreground mt-1">Less than ₦3 a day.</p>
      </div>

      <div className="mt-8 space-y-3">
        {FEATURES.map((f) => (
          <div key={f.text} className="flex items-center gap-3 rounded-2xl bg-card border border-border px-4 py-3">
            <div className="h-8 w-8 rounded-full bg-secondary flex items-center justify-center">
              <f.icon className="h-4 w-4 text-primary" />
            </div>
            <span className="text-sm font-medium text-foreground flex-1">{f.text}</span>
            <Check className="h-4 w-4 text-emerald-600" />
          </div>
        ))}
      </div>

      <p className="mt-6 text-center text-xs text-muted-foreground px-4">
        Sending acknowledgement requests for your active debts, and receiving and responding to them, is free on every plan.
      </p>

      <div className="mt-6">
        {isPlus ? (
          <div data-testid="plan-active-banner" className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4 text-center text-emerald-800 font-semibold">
            Debii Plus · Active{sub?.expires_at ? ` · renews ${fmtDate(sub.expires_at)}` : ""} ✓
          </div>
        ) : androidApp ? (
          <div data-testid="android-billing-panel">
            <GooglePlayPlan
              productId={sub?.google_play_product_id || "debii_plus_annual"}
              onEntitled={refreshUser}
            />
          </div>
        ) : sub?.pending_payment ? (
          <div data-testid="payment-processing-banner" className="rounded-2xl bg-amber-50 border border-amber-200 p-4 text-center text-amber-800">
            <p className="font-semibold">We're confirming your payment</p>
            <p className="mt-1 text-xs">This can take a moment. Please don't make another payment — refresh this page shortly.</p>
          </div>
        ) : (
          <Button data-testid="upgrade-debii-plus-button" onClick={upgrade} disabled={loading} className="w-full h-14 rounded-2xl text-base">
            {loading ? "Starting checkout…" : "Upgrade to Debii Plus"}
          </Button>
        )}
        {sub && !sub.payment_configured && !isPlus && !androidApp && (
          <p data-testid="payment-not-configured-note" className="mt-3 text-center text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
            Payments are integrated with Paystack but need production API keys to process real charges. No payment can be faked.
          </p>
        )}
        {sub && sub.test_mode && !isPlus && !androidApp && (
          <p data-testid="payment-test-mode-note" className="mt-3 text-center text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
            Paystack test mode — use a test card like <b>4084 0840 8408 4081</b>, any future expiry, CVV <b>408</b>. No real money moves.
          </p>
        )}
      </div>
    </div>
  );
}
